import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "lucide-react";
import AccommodationFormDialog from "./AccommodationFormDialog";
import AccommodationDetailDialog from "./AccommodationDetailDialog";
import { fetchRef, getRef, getRefVersion, subscribeRef } from "../paymentDesk/shared/refDataStore";

const MANAGE_ROLES = ["Director", "Manager", "Back Office"];
export const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const DEPOSIT_BADGE = { Pending: "bg-amber-500", Paid: "bg-emerald-600", Refunded: "bg-slate-500" };

// Vendor Master — Accommodation tab. Rooms/flats hired for staff; each has one
// ACC landlord and one Site. Director/Manager/Back Office manage, Accounts views.
export default function AccommodationTab() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [rooms, setRooms] = useState([]);
  useSyncExternalStore(subscribeRef, getRefVersion);
  const sites = getRef("sites").data;
  const vendorOptions = getRef("vendors").data;
  const landlords = useMemo(() => vendorOptions.filter((x) => x.category_code === "ACC"), [vendorOptions]);
  const [loading, setLoading] = useState(true);
  const [siteFilter, setSiteFilter] = useState("all");
  const [landlordFilter, setLandlordFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formTarget, setFormTarget] = useState(null);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => { fetchRef("sites"); fetchRef("vendors"); }, []);

  const load = () => {
    setLoading(true);
    const params = {};
    if (siteFilter !== "all") params.site_id = siteFilter;
    if (landlordFilter !== "all") params.vendor_code = landlordFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    client
      .get("/accommodation/rooms", { params })
      .then((res) => setRooms(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [siteFilter, landlordFilter, statusFilter]);

  return (
    <div className="space-y-4" data-testid="accommodation-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <SearchableSelect testId="accommodation-site-filter" loading={!getRef("sites").loaded} className="w-48" value={siteFilter} onValueChange={setSiteFilter} placeholder="All Sites"
            options={[{ value: "all", label: "All Sites", pinned: true }, ...sites.map((s) => ({ value: s.site_id, label: s.display_label || s.label }))]} />
          <SearchableSelect testId="accommodation-landlord-filter" loading={!getRef("vendors").loaded} className="w-48" value={landlordFilter} onValueChange={setLandlordFilter} placeholder="All Landlords"
            options={[{ value: "all", label: "All Landlords", pinned: true }, ...landlords.map((v) => ({ value: v.vendor_code, label: v.vendor_name, searchText: v.vendor_code }))]} />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="accommodation-status-filter" className="w-36"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Vacated">Vacated</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {canManage && (
          <Button data-testid="accommodation-add-button" onClick={() => setFormTarget("new")} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="h-4 w-4 mr-1.5" /> Add Accommodation
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="accommodation-table">
          <TableHeader>
            <TableRow>
              <TableHead>Room ID</TableHead>
              <TableHead>Landlord</TableHead>
              <TableHead>Site</TableHead>
              <TableHead>Monthly Rent</TableHead>
              <TableHead>Deposit</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : rooms.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No accommodations found.</TableCell></TableRow>
            ) : (
              rooms.map((r) => (
                <TableRow key={r.room_id} data-testid={`accommodation-row-${r.room_id}`} className="cursor-pointer" onClick={() => setDetailId(r.room_id)}>
                  <TableCell className="font-mono text-xs font-semibold">{r.room_id}</TableCell>
                  <TableCell>{r.vendor_name}</TableCell>
                  <TableCell className="text-xs">{r.site_label}</TableCell>
                  <TableCell>{money(r.monthly_rent)}</TableCell>
                  <TableCell><Badge className={DEPOSIT_BADGE[r.deposit_status]}>{r.deposit_status}</Badge></TableCell>
                  <TableCell><Badge className={r.status === "Active" ? "bg-emerald-600" : "bg-slate-400"}>{r.status}</Badge></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {formTarget && (
        <AccommodationFormDialog
          room={formTarget === "new" ? null : formTarget}
          sites={sites}
          landlords={landlords}
          onClose={() => setFormTarget(null)}
          onSaved={() => { setFormTarget(null); load(); }}
          onVendorAdded={() => fetchRef("vendors", { force: true })}
        />
      )}
      {detailId && (
        <AccommodationDetailDialog
          roomId={detailId}
          initial={rooms.find((r) => r.room_id === detailId)}
          canManage={canManage}
          onClose={() => setDetailId(null)}
          onChanged={load}
          onEdit={(room) => { setDetailId(null); setFormTarget(room); }}
        />
      )}
    </div>
  );
}
